import React, { useState } from 'react';
import axios from 'axios';
import { Play, Loader2, Plus, Trash2, MessageSquare } from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const AnnouncementPanel = ({ announcements, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '', description: '', date: '', time: '', venue: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/display/announcements`, formData);
      setShowForm(false);
      setFormData({ title: '', description: '', date: '', time: '', venue: '' });
      onRefresh();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to save announcement');
    }
    setSaving(false);
  };

  const handleShow = async (announcement) => {
    try {
      await axios.post(`${API}/display/show/announcement`, announcement);
      alert('Announcement displayed!');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API}/display/announcements/${id}`);
      onRefresh();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">Announcement Slides</h3>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Add Slide
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {announcements.map(ann => (
          <div key={ann.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
            <h4 className="font-semibold text-slate-800">{ann.title}</h4>
            <p className="text-sm text-slate-600 mt-1">{ann.description}</p>
            {ann.date && <p className="text-xs text-slate-500 mt-2">{ann.date} {ann.time}</p>}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => handleShow(ann)}
                className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm"
              >
                <Play className="w-3 h-3" /> Show
              </button>
              <button
                onClick={() => handleDelete(ann.id)}
                className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">New Announcement</h3>
            <div className="space-y-4">
              <input type="text" placeholder="Title" value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl" />
              <textarea placeholder="Description" value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3} className="w-full px-4 py-2 border border-slate-200 rounded-xl resize-none" />
              <div className="grid grid-cols-2 gap-4">
                <input type="text" placeholder="Date" value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="px-4 py-2 border border-slate-200 rounded-xl" />
                <input type="text" placeholder="Time" value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="px-4 py-2 border border-slate-200 rounded-xl" />
              </div>
              <input type="text" placeholder="Venue" value={formData.venue}
                onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl" />
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowForm(false)}
                className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200">Cancel</button>
              <button onClick={handleSave} disabled={saving || !formData.title}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50">
                {saving ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementPanel;
